import {FC, useState} from 'react';
import {Box, Button, Dialog, Flex, Portal, Text} from '@chakra-ui/react';
import Cropper, {Area, Point} from 'react-easy-crop';
import {Slider, useScreenSize} from 'ui-kit';
import {useT} from '@kcast-fe/core';

const MIN_ZOOM = 1;
const MAX_ZOOM = 3;

interface IProps {
  imageSrc: string;
  isPending?: boolean;
  onCancel: () => void;
  onConfirm: (area: Area) => void;
}

export const AvatarCropDialog: FC<IProps> = ({imageSrc, isPending, onCancel, onConfirm}) => {
  const [crop, setCrop] = useState<Point>({x: 0, y: 0});
  const [zoom, setZoom] = useState(MIN_ZOOM);
  const [croppedArea, setCroppedArea] = useState<Area | null>(null);

  const {isLessThan768} = useScreenSize();
  const {t} = useT();

  const handleConfirm = () => {
    if (croppedArea) {
      onConfirm(croppedArea);
    }
  };

  return (
    <Dialog.Root
      open
      placement={'center'}
      closeOnInteractOutside={!isPending}
      motionPreset={'slide-in-bottom'}
      onOpenChange={(e) => {
        if (!e.open && !isPending) {
          onCancel();
        }
      }}
    >
      <Portal>
        <Dialog.Backdrop bg={'rgba(255,255,255, 0.2)'} />
        <Dialog.Positioner pl={isLessThan768 ? 0 : '326px'} pr={isLessThan768 ? 0 : '12px'}>
          <Dialog.Content maxW={'420px'} borderRadius={'12px'} shadow={'{shadows.modal}'}>
            <Dialog.Body p={'24px'} fontSize={'16px'}>
              <Text mb={'16px'} color={'{colors.textPrimary}'}>
                {t('avatar.cropTitle')}
              </Text>

              <Box
                position={'relative'}
                w={'100%'}
                h={'300px'}
                borderRadius={'12px'}
                overflow={'hidden'}
                bg={'{colors.borderDefault}'}
              >
                <Cropper
                  image={imageSrc}
                  crop={crop}
                  zoom={zoom}
                  aspect={1}
                  minZoom={MIN_ZOOM}
                  maxZoom={MAX_ZOOM}
                  showGrid={false}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={(_, areaPixels) => setCroppedArea(areaPixels)}
                />
              </Box>

              <Box p={'16px 0 40px 0'}>
                <Slider
                  step={0.1}
                  hideMinMax
                  value={zoom}
                  minValue={MIN_ZOOM}
                  maxValue={MAX_ZOOM}
                  onChange={setZoom}
                />
              </Box>

              <Flex justifyContent={'flex-end'} gap={'12px'}>
                <Button
                  minW={'120px'}
                  size={'md'}
                  variant={'subtle'}
                  disabled={isPending}
                  onClick={onCancel}
                >
                  {t('actions.cancel')}
                </Button>
                <Button
                  minW={'120px'}
                  size={'md'}
                  variant={'solid'}
                  loading={isPending}
                  disabled={!croppedArea}
                  onClick={handleConfirm}
                >
                  {t('actions.save')}
                </Button>
              </Flex>
            </Dialog.Body>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};
